import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Spin, Alert, Tag, Empty, Button, Tooltip, Divider } from 'antd';
import { 
  MedicineBoxOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';

const { Title, Text, Paragraph } = Typography;

const PatientPortalMedications = () => {
  const { fetchMedications } = usePatientPortal();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMedications();
  }, []);

  const loadMedications = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchMedications();
      setMedications(data.medications || []);
    } catch (err) {
      setError('Failed to load medications. Please try again.');
      console.error('Error loading medications:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading your medications...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Medications"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadMedications}>
            Retry
          </Button>
        }
      />
    );
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'current':
        return 'green';
      case 'discontinued':
      case 'stopped':
        return 'red';
      case 'paused':
      case 'hold':
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Medication',
      dataIndex: 'medicationName',
      key: 'medicationName',
      width: '25%',
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: '14px' }}>{text}</Text>
          {record.isPrn && (
            <div>
              <Tag color="blue" size="small" style={{ marginTop: '4px' }}>
                PRN (As Needed)
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Dosage & Frequency',
      key: 'dosageInfo',
      width: '25%',
      render: (_, record) => (
        <div>
          <Text strong>{record.dosage}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.frequency}
          </Text>
          {record.route && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Route: {record.route}
              </Text>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: '15%',
      render: (date) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <CalendarOutlined style={{ color: '#8c8c8c' }} />
          <Text style={{ fontSize: '12px' }}>
            {date ? new Date(date).toLocaleDateString() : 'Not specified'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Prescriber',
      dataIndex: 'prescribingProvider',
      key: 'prescribingProvider',
      width: '20%',
      render: (provider) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <UserOutlined style={{ color: '#8c8c8c' }} />
          <Text style={{ fontSize: '12px' }}>
            {provider || 'Not specified'}
          </Text>
        </div>
      ),
    },
  ];

  // Separate PRN and regular medications
  const regularMedications = medications.filter(med => !med.isPrn);
  const prnMedications = medications.filter(med => med.isPrn);
  const activeMedications = medications.filter(med => 
    med.status?.toLowerCase() === 'active' || med.status?.toLowerCase() === 'current'
  );

  return (
    <div>
      <Title level={3}>
        <MedicineBoxOutlined /> Current Medications
      </Title>

      {medications.length === 0 ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No current medications found"
          >
            <Paragraph type="secondary" style={{ textAlign: 'center' }}>
              You currently have no active medications on record.
            </Paragraph>
          </Empty>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                  {activeMedications.length}
                </div>
                <Text type="secondary">Active Medications</Text>
              </div>
            </Card>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {regularMedications.length}
                </div>
                <Text type="secondary">Regular Medications</Text>
              </div>
            </Card>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                  {prnMedications.length}
                </div>
                <Text type="secondary">PRN (As Needed)</Text>
              </div>
            </Card>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                  {medications.length}
                </div>
                <Text type="secondary">Total Medications</Text>
              </div>
            </Card>
          </div>

          {/* Regular Medications */}
          {regularMedications.length > 0 && (
            <Card title="Regular Medications" style={{ marginBottom: '16px' }}>
              <Table
                columns={columns}
                dataSource={regularMedications}
                rowKey="_id"
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
              />
            </Card>
          )}

          {/* PRN Medications */}
          {prnMedications.length > 0 && (
            <Card title="PRN (As Needed) Medications" style={{ marginBottom: '16px' }}>
              <Table
                columns={[
                  ...columns,
                  {
                    title: 'Indication',
                    dataIndex: 'prnIndication',
                    key: 'prnIndication',
                    width: '20%',
                    render: (indication) => (
                      indication ? (
                        <Text style={{ fontSize: '12px' }}>{indication}</Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: '12px' }}>Not specified</Text>
                      )
                    ),
                  },
                ]}
                dataSource={prnMedications}
                rowKey="_id"
                pagination={false}
                size="small"
                scroll={{ x: 900 }}
              />
            </Card>
          )}

          {/* Instructions */}
          {medications.some(med => med.instructions) && (
            <Card title="Special Instructions" style={{ marginBottom: '16px' }}>
              {medications
                .filter(med => med.instructions)
                .map(med => (
                  <div key={med._id} style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <Text strong style={{ fontSize: '13px' }}>{med.medicationName}:</Text>
                    <br />
                    <Text style={{ fontSize: '12px' }}>{med.instructions}</Text>
                  </div>
                ))}
            </Card>
          )}
        </>
      )}

      {/* Important Safety Information */}
      <Card 
        style={{ 
          marginTop: '16px', 
          backgroundColor: '#fff2e8', 
          border: '1px solid #ffbb96' 
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#fa8c16', marginTop: '2px' }} />
          <div>
            <Text strong style={{ color: '#d46b08' }}>Important Medication Safety Information</Text>
            <Paragraph style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#d46b08' }}>
              Please follow these important guidelines for your medications:
            </Paragraph>
            <ul style={{ margin: '8px 0 0 16px', fontSize: '12px', color: '#d46b08' }}>
              <li>Take medications exactly as prescribed by your healthcare provider</li>
              <li>Do not stop or change medications without consulting your provider</li>
              <li>Keep track of when you take your medications</li>
              <li>Report any side effects or concerns to your healthcare team</li>
              <li>Bring all medications (including over-the-counter) to your appointments</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Information Card */}
      <Card 
        style={{ 
          marginTop: '16px', 
          backgroundColor: '#e6f7ff', 
          border: '1px solid #91d5ff' 
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <InfoCircleOutlined style={{ color: '#1890ff', marginTop: '2px' }} />
          <div>
            <Text strong style={{ color: '#0050b3' }}>About Your Medication List</Text>
            <Paragraph style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#0050b3' }}>
              This list shows your current active medications as prescribed by your healthcare team. 
              PRN medications are taken "as needed" for specific symptoms or conditions.
              If you have questions about any medications or experience side effects, 
              please contact your healthcare provider immediately.
            </Paragraph>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PatientPortalMedications;
